import { gql, useMutation } from "@apollo/client";
import { track, identify, Identify } from "@amplitude/analytics-browser";
import {
  UpdateGuestSourceMutation,
  UpdateGuestSourceMutationVariables,
} from "../../../__generated__/graphql";
import { notify, notifyError } from "../../../utils/toast";
import { sources, subSources } from "../constants/source-data";
import { FREQUENCY_COUNT_QUERY } from "../../../api/frequency-queries";
import { GET_GUEST } from "../../../hooks/use-guest-mode";

interface UseSourceSubmissionProps {
  selectedSource: string;
  selectedSubSource: string;
  selectedSubSubSource: string;
  subSourceTexts: Record<string, string>;
  otherSourceText: string;
  onSuccess: () => void;
}

export const UPDATE_GUEST_SOURCE = gql(`
  mutation UpdateGuestSource(
    $source: String
  ) {
    updateGuestSource(
      source: $source
    ) {
      uuid
      loadAddr
      area1
      area2
      area3
      area4
      lat
      lng
      source
    }
  }
`);

export const useSourceSubmission = ({
  selectedSource,
  selectedSubSource,
  selectedSubSubSource,
  subSourceTexts,
  otherSourceText,
  onSuccess,
}: UseSourceSubmissionProps) => {
  const [updateGuest] = useMutation<
    UpdateGuestSourceMutation,
    UpdateGuestSourceMutationVariables
  >(UPDATE_GUEST_SOURCE);

  const formatSourceData = () => {
    /**
     * 포맷방식: Flatten 형식
     *
     * Flatten 형식 출력 예시:
     * 기본 소스 (하위 소스 없음):
     * - "유튜브"
     * - "트위터"
     * - "에브리타임"
     *
     * (하위 소스 있음)
     * - "인스타그램.광고"
     * - "인스타그램.인플루언서.text:김마히"
     * - "인스타그램.지인의포스팅"
     *
     * 형식: [메인소스].[하위소스].[하위하위소스].text:[텍스트입력]
     */
    let finalSource = selectedSource;

    if (selectedSource === "기타") {
      finalSource = `기타.${otherSourceText.trim()}`;
    } else {
      const sourceId = sources.find((s) => s.label === selectedSource)?.id;
      if (sourceId && subSources[sourceId]) {
        // flatten 구조로 변환
        const parts = [selectedSource];

        if (selectedSubSource) {
          const subSource = subSources[sourceId].find(
            (s) => s.id === selectedSubSource
          );
          if (subSource) {
            parts.push(subSource.label);

            if (selectedSubSubSource) {
              const subSubSource = subSource.subSubSources?.find(
                (s) => s.id === selectedSubSubSource
              );
              if (subSubSource) {
                parts.push(subSubSource.label);
              }
            }
          }
        }

        // 텍스트 입력 정보 추가
        Object.entries(subSourceTexts).forEach(([key, value]) => {
          if (value?.trim()) {
            parts.push(`text:${value.trim()}`);
          }
        });

        if (parts.length > 1) {
          finalSource = parts.join(".");
        }
      }
    }

    return finalSource;
  };

  const submitSource = async () => {
    if (!selectedSource) return;

    try {
      const finalSource = formatSourceData();

      // Amplitude에 이벤트 전송
      track("첫 방문 출처 선택", {
        source: finalSource,
        subSource: selectedSubSource,
        subSubSource: selectedSubSubSource,
        subSourceTexts: subSourceTexts,
        timestamp: new Date().toISOString(),
      });

      // identify를 사용해서 userSignupSource 설정
      const identifyUser = new Identify().set("userSignupSource", finalSource);
      identify(identifyUser);

      // localStorage에도 저장 (백업용)
      localStorage.setItem("userSource", finalSource);
      localStorage.setItem("sourceSelectedAt", new Date().toISOString());

      await updateGuest({
        variables: {
          source: finalSource,
        },
        refetchQueries: [FREQUENCY_COUNT_QUERY, GET_GUEST], // 스탬프 2개 반영을 위해서 두 개 모두 필요합니다.!!
      });

      notify("환영합니다 고객님! 스탬프 두 개를 발급해 드렸어요 🎁");
      onSuccess();
    } catch (error) {
      console.error("Error submitting source:", error);
      notifyError("오류가 발생하였습니다. 잠시후 다시 시도해주세요.");
    }
  };

  return {
    submitSource,
  };
};
